import React, { useEffect, useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { useHistory, useParams } from 'react-router-dom';
import {
    askAndGetConnectedMediaDevices,
    filterMediaDevicesBySpecificKind,
} from 'flow/utils/media-utils';
import { generatePath } from 'flow/utils/router-utils';
import {
    setConnectedMediaDevices,
    setPreferredAudioOutputId,
} from 'flow/actions/headset-actions';

import { ExtensionKey } from 'flow/types/EntityKeys';

import { routes } from 'flow/types/routes';

import Page from './Page';
import SettingSection from 'flow/components/Setting/SettingSection';
import DevicePickerRow from './DevicePickerRow';
import { ConnectedMediaDevice } from 'flow/types/Headset';

const SpeakerSettings = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const [error, setError] = useState<any>('');

    const preferredAudioOutputId = useAppSelector((state) =>
        state.headsets.get('preferredAudioOutputId'),
    );

    const audioOutputs = useAppSelector((state) =>
        filterMediaDevicesBySpecificKind(
            state.headsets.get('connectedMediaDevices'),
            'audiooutput',
        ),
    );

    const selectDefaultAudioOutput = audioOutputs.find(
        (audioOutput: ConnectedMediaDevice) =>
            audioOutput?.deviceId === 'default',
    );
    const selectedAudioOutput =
        preferredAudioOutputId || selectDefaultAudioOutput?.deviceId;

    useEffect(() => {
        askAndGetConnectedMediaDevices(true, false)
            .then((devices) => dispatch(setConnectedMediaDevices(devices)))
            .catch((error) => setError(error));
    }, []);

    const getErrorMessage = () => {
        const errorName = error.name || error;

        if (errorName === 'NotAllowedError') {
            return i18next.t('Access to microphone devices denied');
        }

        if (errorName === 'PermissionDismissedError') {
            return i18next.t('Access to microphone devices must be given');
        }

        return `An error occurred: ${errorName}`;
    };

    return (
        <Page
            header="media.settings.audiooutput"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone.audio-in-out'], {
                        extensionKey,
                    }),
                )
            }
        >
            <SettingSection>
                {i18next.t('media.settings.audiooutput')}
            </SettingSection>

            {error ? (
                <div className="text-red">{getErrorMessage()}</div>
            ) : (
                <RadioGroup
                    value={selectedAudioOutput ?? null}
                    aria-label={i18next.t('media.settings.audiooutput')}
                    onChange={(deviceId: string) =>
                        dispatch(setPreferredAudioOutputId(deviceId))
                    }
                >
                    {audioOutputs?.map((audioOutput: ConnectedMediaDevice) => (
                        <DevicePickerRow
                            key={audioOutput.deviceId}
                            deviceId={audioOutput.deviceId}
                            label={
                                audioOutput.label || i18next.t('status.unknown')
                            }
                            selected={
                                audioOutput.deviceId === selectedAudioOutput
                            }
                        />
                    ))}
                </RadioGroup>
            )}
        </Page>
    );
};

export default SpeakerSettings;
